/**
 * DownstreamLineageTree — renders brief → ad packs → asset summary for the
 * Lineage lens.  Performance ROAS / fatigue overlays land in Phase 2; for
 * Phase 1 we show counts + status + a deep-link to each ad pack.
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, Image, Layers, Sparkles, Zap } from 'lucide-react';
import type { GenerationBrief, CampaignDetailResponse } from '@/services/api';
import { useGenerationStore } from '@/stores/generation';

interface Props {
  brief: GenerationBrief;
}

export function DownstreamLineageTree({ brief }: Props) {
  const { campaignDetails, fetchCampaignDetail } = useGenerationStore();
  const playbookIds = brief.playbook_ids ?? [];

  useEffect(() => {
    for (const pid of playbookIds) {
      if (!campaignDetails[pid]) fetchCampaignDetail(pid);
    }
  }, [playbookIds, campaignDetails, fetchCampaignDetail]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <h3 className="text-sm font-bold text-slate-800 flex items-center gap-2 mb-3">
        <Layers size={14} className="text-slate-400" />
        Downstream lineage
      </h3>

      {playbookIds.length === 0 ? (
        <p className="text-[11px] text-slate-400">
          No ad packs generated from this brief yet.
        </p>
      ) : (
        <div className="space-y-3">
          <div className="text-[11px] text-slate-600">
            <span className="font-semibold text-slate-800">{brief.name}</span> →{' '}
            <span className="text-slate-500">
              {playbookIds.length} ad pack{playbookIds.length !== 1 ? 's' : ''}
            </span>
          </div>
          <ul className="space-y-2">
            {playbookIds.map((pid) => (
              <PackBranch
                key={pid}
                pid={pid}
                detail={campaignDetails[pid]}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function PackBranch({
  pid,
  detail,
}: {
  pid: string;
  detail?: CampaignDetailResponse;
}) {
  const navigate = useNavigate();
  const status = detail?.status ?? 'loading';
  const isRunning = ['running', 'pending', 'queued'].includes(status);

  const angleCount = detail?.asset_summary?.total_angles ?? 0;
  const imageCount = detail?.asset_summary?.total_images ?? 0;
  const adCount = detail?.asset_summary?.total_static_ads ?? 0;

  return (
    <li>
      <button
        type="button"
        onClick={() => navigate(`/generation/${pid}`)}
        className="w-full text-left rounded-lg border border-slate-200 hover:border-emerald-300 hover:shadow-sm transition p-3 group"
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <div
              className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                isRunning
                  ? 'bg-amber-50 text-amber-600'
                  : 'bg-emerald-50 text-emerald-600'
              }`}
            >
              <Zap size={12} />
            </div>
            <div className="min-w-0">
              <div className="font-mono text-[11px] text-slate-700 truncate">
                {pid}
              </div>
              <div className="text-[10px] text-slate-500 capitalize">{status}</div>
            </div>
          </div>
          <ChevronRight
            size={14}
            className="text-slate-400 shrink-0 group-hover:text-emerald-500"
          />
        </div>

        {detail && !isRunning && (
          <div className="mt-2 grid grid-cols-3 gap-2">
            <Tile icon={Sparkles} count={angleCount} label="Angles" />
            <Tile icon={Image} count={imageCount} label="Images" />
            <Tile icon={Zap} count={adCount} label="Ads" />
          </div>
        )}

        {isRunning && detail?.run_progress && (
          <div className="mt-2">
            <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
              <span className="truncate max-w-[160px]">
                {detail.run_progress.current_stage}
              </span>
              <span className="font-bold">
                {detail.run_progress.percent_complete}%
              </span>
            </div>
            <div className="bg-amber-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="bg-amber-500 h-full rounded-full transition-all duration-500"
                style={{
                  width: `${detail.run_progress.percent_complete}%`,
                }}
              />
            </div>
          </div>
        )}
      </button>
    </li>
  );
}

function Tile({
  icon: Icon,
  count,
  label,
}: {
  icon: typeof Image;
  count: number;
  label: string;
}) {
  return (
    <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-50 rounded">
      <Icon size={10} className="text-slate-400" />
      <span className="text-[10px] font-bold text-slate-700">{count}</span>
      <span className="text-[9px] text-slate-500">{label}</span>
    </div>
  );
}
